import React, { useState, useEffect } from "react";
import {
  Box, Typography, Container, Grid, Stack, Button, Card, Chip,
  Avatar, alpha, useMediaQuery, useTheme
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@mui/system";

// Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import PeopleIcon from "@mui/icons-material/People";
import SpeedIcon from "@mui/icons-material/Speed";
import CloudIcon from "@mui/icons-material/Cloud";
import BarChartIcon from "@mui/icons-material/BarChart";
import InventoryIcon from "@mui/icons-material/Inventory";
import TimelineIcon from "@mui/icons-material/Timeline";
import BusinessIcon from "@mui/icons-material/Business";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BuildIcon from "@mui/icons-material/Build";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

import Navbar from "./Navbar";
import HomeFooter from "./HomeFooter";
import Lider from "./Lider";

// Animations
const glowAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.5); }
`;

// Animation keyframes
const floatAnimation = `
  @keyframes floatAnimation {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    25% { transform: translateY(-20px) translateX(10px); }
    50% { transform: translateY(-30px) translateX(-10px); }
    75% { transform: translateY(-10px) translateX(20px); }
  }
`;

// Data
const features = [
  {
    title: "HRM Module",
    desc: "Manage Employee, Payroll , Recuirtment & Leave.",
    image: "HRM1.jpg",
    points: [
      "✅ Employee Management – Add, update and manage employee records.",
      "✅ Payroll Processing – Automate salary calculations, deductions and tax compliance.",
      "✅ Leave & Attendance – Track employee attendance and leave requests.",
      "✅ Recruitment Management – Streamline hiring and onboarding processes.",
    ],
    icon: <PeopleIcon />,
    color: "#3b82f6",
  },
  {
    title: "Finance & Accounting",
    desc: "Automate invoice, Expenses & Financial Reports.",
    image: "Finance.png",
    points: [
      "✅ Automate Invoice – Generate and send invoices with automated tax calculations.",
      "✅ Expense Tracking – Monitor business expenses and categorize them efficiently.",
      "✅ Financial Reporting – Get real-time balance sheets, profit & loss reports.",
      "✅ Multi-Currency Support – Handle transactions in different currencies.",
    ],
    icon: <BarChartIcon />,
    color: "#10b981",
  },
  {
    title: "Sales & CRM",
    desc: "Handle Customer Relationship, Sales Lead and Invoice.",
    image: "CRM.png",
    points: [
      "✅ Customer Relationship Management – Maintain customer history and interactions.",
      "✅ Lead & Opportunity Tracking – Monitor sales pipeline and conversions.",
      "✅ Quotation & Billing – Generate professional quotations and invoices.",
      "✅ Integration with E-commerce – Sync with online sales channels.",
    ],
    icon: <TimelineIcon />,
    color: "#8b5cf6",
  },
  {
    title: "User-Friendly Dashboard",
    desc: "Get real-time insights and reports with an interactive dashboard.",
    image: "userfriendly.jpg",
    points: [
      "✅ Real-time Insights – View key performance metrics at a glance.",
      "✅ Customizable Reports – Generate reports based on business needs.",
      "✅ Role-Based Access – Provide different views for employees, managers and admins.",
      "✅ Mobile Access – Monitor business performance on the go.",
    ],
    icon: <AnalyticsIcon />,
    color: "#ef4444",
  },
  {
    title: "Project Management",
    desc: "Plan, Track and Collaborate on projects efficiently.",
    image: "ProjectManage.jpg",
    points: [
      "✅ Task Management – Assign tasks, set deadlines and track progress.",
      "✅ Time Tracking – Log work hours for accurate project estimation.",
      "✅ Collaboration Tools – Share files, notes and updates in real-time.",
      "✅ Agile & Scrum Support – Manage sprints and backlogs with ease.",
    ],
    icon: <BusinessIcon />,
    color: "#06b6d4",
  },
  {
    title: "Procurement",
    desc: "Optimize purchasing and vendor management processes.",
    image: "Procurement.jpg",
    points: [
      "✅ Purchase Orders – Automate vendor orders and approvals.",
      "✅ Supplier Management – Maintain a database of trusted suppliers.",
      "✅ Budget Control – Track spending and avoid unnecessary costs.",
      "✅ Procurement Analytics – Get insights into purchasing trends.",
    ],
    icon: <ShoppingCartIcon />,
    color: "#84cc16",
  },
  {
    title: "Manufacturing",
    desc: "Track production processes and optimize workflows.",
    image: "Manufacturing.jpg",
    points: [
      "✅ Production Planning – Schedule and manage manufacturing processes.",
      "✅ Work Order Management – Track work orders from start to completion.",
      "✅ Quality Control – Ensure product quality with inspections and checks.",
      "✅ Equipment Maintenance – Schedule and monitor machinery maintenance.",
    ],
    icon: <BuildIcon />,
    color: "#f97316",
  },
  {
    title: "Reporting & Analytics",
    desc: "Make data-driven decisions with powerful insights.",
    image: "Reporting & Analytics.jpg",
    points: [
      "✅ Business Intelligence – Use AI-powered analytics for forecasting.",
      "✅ Custom Reports – Generate reports tailored to business needs.",
      "✅ Data Visualization – Get insights using charts and graphs.",
      "✅ Export & Integration – Export reports in Excel, PDF or CSV.",
    ],
    icon: <AutoAwesomeIcon />,
    color: "#ec4899",
  },
];

const statistics = [
  { value: "40", label: "Cost Reduction", icon: <TrendingUpIcon />, color: "#3b82f6", suffix: "%" },
  { value: "500", label: "Happy Clients", icon: <PeopleIcon />, color: "#10b981", suffix: "+" },
  { value: "99.9", label: "Uptime", icon: <CloudIcon />, color: "#f59e0b", suffix: "%" },
  { value: "85", label: "Efficiency Gain", icon: <SpeedIcon />, color: "#8b5cf6", suffix: "%" },
];

const whyChooseUs = [
  { title: "Scalable Solution", description: "Grow with confidence,Our system scales seamlessly with your business.", icon: <TrendingUpIcon fontSize="large" /> },
  { title: "24/7 Support", description: "Round-the-clock technical support to ensure your business never stops.", icon: <SupportAgentIcon fontSize="large" /> },
  { title: "Security", description: "Advanced security standards to protect your data and privacy.", icon: <SecurityIcon fontSize="large" /> },
  { title: "Easy Integration", description: "Integrates effortlessly with your existing tools and platforms.", icon: <IntegrationInstructionsIcon fontSize="large" /> },
];

const techStack = [
  { name: "React", color: "#61dafb", logo: "⚛️" },
  { name: "Node.js", color: "#68a063", logo: "🟢" },
  { name: "PostgreSQL", color: "#336791", logo: "🐘" },
  { name: "AWS", color: "#ff9900", logo: "☁️" },
  { name: "Docker", color: "#2496ed", logo: "🐳" },
  { name: "Redis", color: "#d82c20", logo: "🧠" },
  { name: "MongoDB", color: "#47a248", logo: "🍃" },
  { name: "Python", color: "#3776ab", logo: "🐍" },
];

const testimonials = [
  { name: "Alex Johnson", role: "CTO, TechCorp Solutions", content: "LiderERP transformed our operations. The seamless integration between modules saved us 40% in administrative costs.", avatar: "A", rating: 5, company: "TechCorp" },
  { name: "Sarah Williams", role: "Operations Manager, RetailPlus", content: "The inventory management module is exceptional. Real-time tracking has reduced our stockouts by 85%.", avatar: "S", rating: 5, company: "RetailPlus" },
  { name: "Michael Chen", role: "Finance Director, GlobalTrade Inc", content: "Financial reporting has never been easier. The automated insights help us make better decisions faster.", avatar: "M", rating: 4, company: "GlobalTrade" },
  { name: "Emma Rodriguez", role: "HR Director, PeopleFirst Corp", content: "Our HR processes are now 60% more efficient. The payroll automation alone was worth the investment.", avatar: "E", rating: 5, company: "PeopleFirst" },
];

const LiderERP = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [counters, setCounters] = useState({});

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animated counter
  useEffect(() => {
    statistics.forEach((stat) => {
      let counter = 0;
      const timer = setInterval(() => {
        counter += 1;
        setCounters(prev => ({ ...prev, [stat.label]: counter }));
        if (counter >= parseInt(stat.value)) clearInterval(timer);
      }, 30);
    });
  }, []);

  // Mouse tracking for gradient (disable on mobile for performance)
  useEffect(() => {
    if (isMobile) return;

    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  const GradientBackground = () => (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: `
        radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(6, 182, 212, 0.05) 0%, transparent 50%),
        linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)
      `,
      zIndex: -1,
      transform: !isMobile ? `translate(${mousePosition.x}px, ${mousePosition.y}px)` : 'none',
      transition: 'transform 0.3s ease-out',
    }} />
  );

  const FloatingParticles = () => (
    <Box sx={{
      position: 'absolute',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
      display: { xs: 'none', md: 'block' } // Hide on mobile for performance
    }}>
      {[...Array(isMobile ? 5 : 15)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * (isMobile ? 5 : 10) + 2,
            height: Math.random() * (isMobile ? 5 : 10) + 2,
            background: `rgba(59, 130, 246, ${Math.random() * 0.15})`,
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{ y: [0, -30, 0], x: i % 2 ? [0, 20, 0] : [0, -20, 0] }}
          transition={{ duration: Math.random() * 10 + 10, repeat: Infinity }}
        />
      ))}
    </Box>
  );

  const imageSet = ["Ac5.jpg", "Ac6.jpg", "Ac7.avif", "Ac8.jpg", "Ac9.jpg", "Ac10.jpg"];

  return (
    <Box sx={{
      backgroundColor: "#f8fafc",
      color: "#1e293b",
      overflow: "hidden",
      position: "relative",
      minHeight: "100vh",
      width: '100%'
    }}>
      <GradientBackground />
      <FloatingParticles />
      <Navbar />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)",
          width: '100%'
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 10% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 40%),
              radial-gradient(circle at 90% 30%, rgba(236, 72, 153, 0.05) 0%, transparent 40%),
              radial-gradient(circle at 30% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 40%),
              radial-gradient(circle at 70% 70%, rgba(16, 185, 129, 0.05) 0%, transparent 40%)
            `,
            zIndex: 0
          }}
        />

        <Box
  sx={{
    flex: 1,
    display: "flex",
    alignItems: "center",
    position: "relative",

    // Navbar fixed/absolute overlap avoid
    pt: { xs: 12, sm: 10, md: 8 },
    pb: { xs: 4, sm: 6, md: 8 },

    width: "100%",
    overflow: "hidden",
  }}
>
          <Container maxWidth="xl">
            <Grid
              container
              spacing={4}
              alignItems="center"
              justifyContent="center"
              direction={{ xs: "column", md: "row" }}
            >
              
              {/* LEFT SIDE: Title & Content (Text) */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: {
                        xs: "2.2rem",
                        sm: "2.8rem",
                        md: "4rem",
                      },
                      lineHeight: 1.1,
                      textAlign: {
                        xs: "center",
                        md: "left",
                      },
                      mb: 2,
                      color: "#4551be",
                    }}
                  >
                    WELCOME TO LIDER TECHNOLOGY
                  </Typography>

                  <Typography
                    sx={{
                      color: "#475569",
                      fontSize: {
                        xs: "1rem",
                        md: "1.15rem",
                      },
                      lineHeight: 1.8,
                      textAlign: {
                        xs: "center",
                        md: "left",
                      },
                      mb: 4,
                    }}
                  >
                    Empowering business with{" "}
                    <strong>
                      innovative IT solutions
                    </strong>{" "}
                    that drive growth, efficiency and digital
                    transformation.
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      justifyContent: {
                        xs: "center",
                        md: "flex-start",
                      },
                    }}
                  >
                    {[
                      "Real-time Analytics",
                      "Cloud Integration",
                      "Secure Data Management",
                      "24/7 Support",
                    ].map((text, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          bgcolor: "#fff",
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            color: "#10b981",
                            fontSize: 20,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: {
                              xs: "0.8rem",
                              md: "0.9rem",
                            },
                            fontWeight: 500,
                            color: "#475569",
                          }}
                        >
                          {text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </motion.div>
              </Grid>

              {/* RIGHT SIDE: Image */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: { xs: 2, md: 0 },
                  }}
                >
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/images/Aboutgpt.png`}
                    alt="Lider Technology"
                    sx={{
                      width: {
                        xs: "100%",
                        sm: "100%",
                        md: "100%",
                      },
                      maxWidth: {
                        xs: 380,
                        sm: 350,
                        md: 650,
                      },
                      height: "auto",
                      marginTop: "70px",
                      animation: {
                        xs: "none",
                        md: "float 6s ease-in-out infinite",
                      },
                      "@keyframes float": {
                        "0%, 100%": {
                          transform: "translateY(0px)",
                        },
                        "50%": {
                          transform: "translateY(-15px)",
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>

            </Grid>
          </Container>
        </Box>
      </Box>
      <style>
        {`
          @keyframes floatAnimation {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            25% { transform: translateY(-20px) translateX(10px); }
            50% { transform: translateY(-30px) translateX(-10px); }
            75% { transform: translateY(-10px) translateX(20px); }
          }
        `}
      </style>
      
      {/* Why Choose Us */}
      <Box sx={{ py: { xs: 6, sm: 8, md: 12 }, background: "white" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h2" sx={{
              textAlign: "center",
              fontWeight: 800,
              background: "linear-gradient(135deg, #1e293b, #334155)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem', lg: '3.2rem' }
            }}>
              Why Choose Lider?
            </Typography>
            <Typography variant="h5" sx={{
              textAlign: "center",
              color: "#64748b",
              mb: { xs: 4, sm: 6, md: 10 },
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' }
            }}>
              Experience the difference with our comprehensive enterprise solution
            </Typography>

            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              {whyChooseUs.map((item, i) => {
                const colors = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4"];
                const color = colors[i % colors.length];

                return (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      whileHover={{ y: isMobile ? 0 : -10 }}
                    >
                      <Card sx={{
                        p: { xs: 3, sm: 4 },
                        textAlign: "center",
                        borderRadius: 4,
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                        background: `linear-gradient(135deg, ${alpha(color, 0.15)}, ${alpha(color, 0.05)})`,
                        border: `1px solid ${alpha(color, 0.3)}`,
                        backdropFilter: "blur(10px)",
                        transition: "all 0.3s ease",
                        '&:hover': {
                          transform: { md: "translateY(-10px) scale(1.02)" },
                          boxShadow: `0 20px 50px ${alpha(color, 0.4)}`
                        },
                        '&::before': {
                          content: '""',
                          position: "absolute",
                          width: { xs: "80px", sm: "100px", md: "120px" },
                          height: { xs: "80px", sm: "100px", md: "120px" },
                          background: color,
                          borderRadius: "50%",
                          top: "-30px",
                          right: "-30px",
                          opacity: 0.15,
                          filter: "blur(40px)"
                        }
                      }}>
                        {/* ICON */}
                        <Box sx={{
                          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                          mb: { xs: 2, sm: 3 },
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: 50, sm: 60, md: 70 },
                          height: { xs: 50, sm: 60, md: 70 },
                          borderRadius: "50%",
                          color: color,
                          background: alpha(color, 0.15),
                          boxShadow: `0 10px 20px ${alpha(color, 0.3)}`
                        }}>
                          {item.icon}
                        </Box>

                        {/* TITLE */}
                        <Typography variant="h5" sx={{
                          fontWeight: 700,
                          mb: 2,
                          color: "#1e293b",
                          fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
                        }}>
                          {item.title}
                        </Typography>

                        {/* DESCRIPTION */}
                        <Typography sx={{
                          color: "#475569",
                          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.95rem' },
                          lineHeight: 1.6
                        }}>
                          {item.description}
                        </Typography>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      <Lider />
      <HomeFooter />
    </Box>
  );
};

export default LiderERP;